import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Currencies = () => {
  const navigate = useNavigate();
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/currencies/');
      setCurrencies(response.data.currencies || []);
    } catch (error) {
      console.error('Error fetching currencies:', error);
      setError('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedCurrencies = async () => {
    try {
      const response = await api.post('/currencies/seed');
      alert(response.data.message);
      fetchCurrencies();
    } catch (error) {
      console.error('Error seeding currencies:', error);
      alert('Failed to seed currencies');
    }
  };

  const handleDelete = async (currency) => {
    if (!window.confirm(`Are you sure you want to delete ${currency.name}?`)) {
      return;
    }
    
    try {
      await api.delete(`/currencies/${currency.id}`);
      fetchCurrencies();
    } catch (error) {
      console.error('Error deleting currency:', error);
      alert(error.response?.data?.error || 'Failed to delete currency');
    }
  };

  const handleSetDefault = async (currency) => {
    try {
      await api.post(`/currencies/${currency.id}/set-default`);
      fetchCurrencies();
    } catch (error) {
      console.error('Error setting default currency:', error);
      alert(error.response?.data?.error || 'Failed to set default currency');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/dashboard')}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Currencies
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              Currencies
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Manage currencies and exchange rates
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleSeedCurrencies}
              className="btn btn-secondary"
            >
              📥 Seed Defaults
            </button>
            <button
              onClick={() => navigate('/currencies/new')}
              className="btn btn-primary"
            >
              ➕ Add Currency
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {currencies.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            color: 'var(--gray-500)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💱</div>
            <h3 style={{ color: 'var(--gray-700)', marginBottom: '0.5rem' }}>No currencies configured</h3>
            <p style={{ marginBottom: '1rem' }}>Add currencies manually or seed default currencies to get started.</p>
            <button onClick={handleSeedCurrencies} className="btn btn-primary">
              📥 Seed Default Currencies
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Currency</th>
                  <th>Code</th>
                  <th>Symbol</th>
                  <th>Exchange Rate</th>
                  <th>Status</th>
                  <th style={{ width: '150px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currencies.map((currency) => (
                  <tr key={currency.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontWeight: 500 }}>{currency.name}</span>
                        {currency.is_default && (
                          <span className="badge badge-primary" style={{ marginLeft: '0.5rem' }}>
                            Default
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <strong>{currency.code}</strong>
                    </td>
                    <td style={{ fontSize: '1.25rem' }}>
                      {currency.symbol}
                    </td>
                    <td>
                      {currency.exchange_rate ? currency.exchange_rate.toFixed(4) : '1.0000'}
                    </td>
                    <td>
                      {currency.is_active ? (
                        <span className="badge badge-success">✓ Active</span>
                      ) : (
                        <span className="badge badge-secondary">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        {!currency.is_default && (
                          <button
                            onClick={() => handleSetDefault(currency)}
                            className="btn btn-sm btn-secondary"
                            title="Set as Default"
                          >
                            ⭐
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/currencies/${currency.id}/edit`)}
                          className="btn btn-sm btn-secondary"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(currency)}
                          className="btn btn-sm btn-danger"
                          title="Delete"
                          disabled={currency.is_default}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Currencies;
